import React, { useState } from 'react';
import { FaUser, FaImage } from 'react-icons/fa';
import CustomModal from './CustomModal';
import { API_ENDPOINTS } from '../config/backendConfig';

const GenerateArtworkModal = ({
  isOpen,
  onClose,
  allCharacters = [],
  storyId,
  onArtworkGenerated,
  currentUser,
  currentSceneIndex
}) => {
  const [selectedCharacterIds, setSelectedCharacterIds] = useState([]);
  const [sceneDescription, setSceneDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  // Debug logging for selection
  console.log('=== GENERATE ARTWORK MODAL DEBUG ===');
  console.log('allCharacters:', allCharacters);
  console.log('allCharacters details:', allCharacters.map(c => ({
    id: c.id,
    name: c.name,
    imageUrl: c.imageUrl,
    hasImageUrl: !!c.imageUrl,
    type: c.type
  })));
  console.log('selectedCharacterIds:', selectedCharacterIds);
  // Helper: get selected character objects
  const selectedCharacters = allCharacters.filter(c => selectedCharacterIds.includes(c.id || c.name));
  console.log('selectedCharacters:', selectedCharacters);
  console.log('selectedCharacters details:', selectedCharacters.map(c => ({
    id: c.id,
    name: c.name,
    imageUrl: c.imageUrl,
    hasImageUrl: !!c.imageUrl
  })));

  const handleCharacterToggle = (id) => {
    setSelectedCharacterIds(prev =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  const handleGenerate = async () => {
    if (selectedCharacters.length === 0) {
      setModal({ isOpen: true, title: 'Select Characters', message: 'Please select at least one character to include in the scene.', type: 'warning' });
      return;
    }
    if (!sceneDescription.trim()) {
      setModal({ isOpen: true, title: 'Describe the Scene', message: 'Please provide a description for the scene/background.', type: 'warning' });
      return;
    }
    setGenerating(true);
    setGeneratedImageUrl(null);
    try {
      let imageUrlToUse;
      if (selectedCharacters.length === 1) {
        // Single character: use their image directly, no merge needed
        console.log('Single character selected, using image directly:', selectedCharacters[0].imageUrl);
        imageUrlToUse = selectedCharacters[0].imageUrl;
      } else if (selectedCharacters.length > 1) {
        // Multiple characters: merge their images first
        console.log('Selected characters for merge:', selectedCharacters.map(c => c.name));
        console.log('Image URLs for merge:', selectedCharacters.map(c => c.imageUrl));
        console.log('Multiple characters selected, merging images...');
        let mergeRes, mergeData;
        try {
          mergeRes = await fetch(`${API_ENDPOINTS.backendBase}/api/character-consistency-refine/merge-many`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ urls: selectedCharacters.map(c => c.imageUrl) })
          });
        } catch (err) {
          console.error('Network error during merge:', err);
          setModal({ isOpen: true, title: 'Merge Failed', message: 'Network error during character image merge. Please try again.', type: 'error' });
          setGenerating(false);
          return;
        }
        if (!mergeRes.ok) {
          const errorText = await mergeRes.text();
          console.error('Merge failed:', errorText);
          setModal({ isOpen: true, title: 'Merge Failed', message: `Failed to merge character images: ${errorText}`, type: 'error' });
          setGenerating(false);
          return;
        }
        try {
          mergeData = await mergeRes.json();
        } catch (err) {
          console.error('Failed to parse merge response as JSON:', err);
          setModal({ isOpen: true, title: 'Merge Failed', message: 'Failed to parse merge response. Please try again.', type: 'error' });
          setGenerating(false);
          return;
        }
        console.log('Merge response:', mergeData);
        if (!mergeData.mergedImageUrl) {
          setModal({ isOpen: true, title: 'Merge Failed', message: 'No merged image URL returned from merge API.', type: 'error' });
          setGenerating(false);
          return;
        }
        imageUrlToUse = mergeData.mergedImageUrl;
        console.log('Merged image URL:', imageUrlToUse);
      } else {
        setModal({ isOpen: true, title: 'No Characters Selected', message: 'Please select at least one character.', type: 'error' });
        setGenerating(false);
        return;
      }
      // Send to BFL for generation
      const bflRequestBody = {
        imageUrl: imageUrlToUse,
        refineInstructions: sceneDescription,
        artistName: currentUser?.displayName || 'Young Artist',
        userId: currentUser?.uid || 'anonymous',
        storyId,
        characterIds: selectedCharacters.map(c => c.id),
        taggedCharacters: selectedCharacters
      };
      console.log('=== BFL API CALL ===');
      console.log('Endpoint:', `${API_ENDPOINTS.backendBase}/api/refine-artwork`);
      console.log('Request body:', bflRequestBody);
      console.log('Selected characters:', selectedCharacters);
      console.log('Image URL to use:', imageUrlToUse);
      let bflRes, bflData;
      try {
        bflRes = await fetch(`${API_ENDPOINTS.backendBase}/api/refine-artwork`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bflRequestBody)
        });
      } catch (err) {
        console.error('Network error during BFL call:', err);
        setModal({ isOpen: true, title: 'Generation Failed', message: 'Network error during artwork generation. Please try again.', type: 'error' });
        setGenerating(false);
        return;
      }
      console.log('BFL response status:', bflRes.status);
      console.log('BFL response ok:', bflRes.ok);
      if (!bflRes.ok) {
        let errorMessage = 'Failed to generate artwork with BFL';
        try {
          const errorData = await bflRes.json();
          console.log('BFL error response:', errorData);
          console.log('BFL error message:', errorData.error || errorData.message);
          console.log('BFL error details:', errorData.details);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          const errorText = await bflRes.text();
          console.log('Could not parse BFL error response as JSON:', errorText);
          errorMessage = errorText || errorMessage;
        }
        setModal({ isOpen: true, title: 'Generation Failed', message: errorMessage, type: 'error' });
        setGenerating(false);
        return;
      }
      try {
        bflData = await bflRes.json();
      } catch (err) {
        console.error('Failed to parse BFL response as JSON:', err);
        setModal({ isOpen: true, title: 'Generation Failed', message: 'Failed to parse BFL response. Please try again.', type: 'error' });
        setGenerating(false);
        return;
      }
      console.log('BFL success response:', bflData);
      if (!bflData.refinedImageUrl) {
        setModal({ isOpen: true, title: 'Generation Failed', message: 'No image returned from BFL', type: 'error' });
        setGenerating(false);
        return;
      }
      setGeneratedImageUrl(bflData.refinedImageUrl);
      if (onArtworkGenerated) {
        onArtworkGenerated(bflData.refinedImageUrl, selectedCharacters, sceneDescription);
      }
      setModal({ isOpen: true, title: 'Artwork Generated!', message: 'Your artwork has been generated successfully!', type: 'success' });
    } catch (err) {
      console.error('Error in handleGenerate:', err);
      setModal({ isOpen: true, title: 'Generation Failed', message: err.message || 'Failed to generate artwork.', type: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateArtwork = () => {
    if (generatedImageUrl) {
      onArtworkGenerated(generatedImageUrl, selectedCharacters, sceneDescription);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-amber-200">
            <h3 className="text-xl font-bold text-amber-800">🎨 Generate New Scene Artwork</h3>
            <button
              className="text-gray-500 hover:text-gray-700 text-2xl"
              onClick={onClose}
              title="Close"
            >
              ×
            </button>
          </div>
          <div className="flex flex-col lg:flex-row h-full" style={{ height: 'calc(95vh - 100px)' }}>
            {/* Left: Generated artwork placeholder */}
            <div className="lg:w-1/2 p-6 flex flex-col items-center justify-center">
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl overflow-hidden relative flex items-center justify-center" style={{ aspectRatio: '1', maxHeight: '60vh', minHeight: '300px', width: '100%' }}>
                {generating && (
                  <div className="flex flex-col items-center justify-center w-full h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mb-4"></div>
                    <span className="text-amber-600 text-lg">Generating artwork...</span>
                  </div>
                )}
                {!generating && generatedImageUrl && (
                  <img src={generatedImageUrl} alt="Generated Artwork" className="w-full h-full object-contain" />
                )}
                {!generating && !generatedImageUrl && (
                  <div className="flex flex-col items-center justify-center w-full h-full">
                    <FaImage className="text-amber-300 text-6xl mb-2" />
                    <span className="text-amber-600 text-base">Your generated artwork will appear here</span>
                  </div>
                )}
              </div>
            </div>
            {/* Right: Character selection and prompt */}
            <div className="lg:w-1/2 p-6 bg-amber-50 border-l border-amber-200 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 100px)' }}>
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-amber-800 mb-2">Select Characters for the Scene</h4>
                <div className="flex flex-wrap gap-2 mb-2">
                  {allCharacters.length === 0 && <span className="text-amber-600">No character tags available yet.</span>}
                  {allCharacters.map((char, idx) => (
                    <label key={char.id || char.name || idx} className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 ${selectedCharacterIds.includes(char.id || char.name) ? 'border-amber-500 bg-white' : 'border-amber-200 bg-amber-100'} cursor-pointer transition-all duration-200`}>
                      <input
                        type="checkbox"
                        checked={selectedCharacterIds.includes(char.id || char.name)}
                        onChange={() => handleCharacterToggle(char.id || char.name)}
                        className="accent-amber-500"
                      />
                      <FaUser className="text-amber-500" />
                      <span className="font-medium">{char.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-amber-800 mb-2">
                  ✍️ Describe what is happening in the scene, background, etc.
                </label>
                <textarea
                  className="w-full border rounded p-3 min-h-[120px] resize-none"
                  value={sceneDescription}
                  onChange={e => setSceneDescription(e.target.value)}
                  placeholder="Describe the scene, what the characters are doing, the background, etc."
                  disabled={generating}
                />
              </div>
              <div className="flex flex-col gap-3">
                <button
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-400 text-white font-semibold py-3 px-4 rounded-xl shadow hover:from-amber-600 hover:to-amber-500 transition-all duration-300"
                  onClick={handleGenerate}
                  disabled={generating}
                >
                  {generating ? 'Generating...' : '✨ Generate Artwork'}
                </button>
                {generatedImageUrl && (
                  <button
                    className="w-full bg-gradient-to-r from-green-500 to-green-400 text-white font-semibold py-3 px-4 rounded-xl shadow hover:from-green-600 hover:to-green-500 transition-all duration-300"
                    onClick={handleUpdateArtwork}
                  >
                    Update Artwork
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomModal
        isOpen={modal.isOpen}
        onClose={() => setModal({ isOpen: false, title: '', message: '', type: 'info' })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </>
  );
};

export default GenerateArtworkModal; 